import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import api from '../services/api';
import './ReportingAPV.css';
import './RingoverReporting.css';

// ── Helpers période ─────────────────────────────────────────────────────────

const PRESETS = [
  { id: '7j',            label: '7 j' },
  { id: '30j',           label: '30 j' },
  { id: '90j',           label: '90 j' },
  { id: 'mois-en-cours', label: 'Mois en cours' },
  { id: 'mois-dernier',  label: 'Mois dernier' },
  { id: 'trimestre',     label: 'Trimestre' },
  { id: 'annee',         label: 'Année' },
];

const SERVICES = [
  { code: '',         nom: 'Tous les services' },
  { code: 'ATELIER',  nom: 'Atelier' },
  { code: 'PIECES',   nom: 'Pièces' },
  { code: 'VN',       nom: 'VN' },
  { code: 'VO',       nom: 'VO' },
  { code: 'AUTRE',    nom: 'Autres' },
  { code: 'NON_ATTRIBUE', nom: 'Non attribué' },
];

const SERVICE_COLORS = {
  ATELIER:       '#0891b2',
  PIECES:        '#3b82f6',
  VN:            '#22c55e',
  VO:            '#f59e0b',
  AUTRE:         '#a855f7',
  NON_ATTRIBUE:  '#94a3b8',
};

const SERVICE_NOMS = SERVICES.reduce((acc, s) => {
  if (s.code) acc[s.code] = s.nom;
  return acc;
}, {});

const TABS = [
  { id: 'direct',   label: '🔴 Direct' },
  { id: 'qualite',  label: '📊 Qualité de service' },
  { id: 'conso',    label: '📞 Consommation par standard' },
  { id: 'services', label: '🧭 Services & motifs' },
];

// Familles de statuts Ringover → présentation dans le flux d'appels.
const ANSWERED_TYPES = ['ANSWERED', 'OUT_ANSWERED', 'BLIND_TRANSFERED', 'VOICEMAIL_ANSWERED'];
function callStatusMeta(direction, callType) {
  const t = (callType || '').toUpperCase();
  if (t === 'RINGING')                                    return { label: 'en ligne…', cls: 'live' };
  if (ANSWERED_TYPES.includes(t))                         return { label: direction === 'outbound' ? 'sortant' : 'décroché', cls: 'ok' };
  if (['MISSED', 'QUEUE_TIMEOUT'].includes(t))            return { label: 'manqué', cls: 'ko' };
  if (['VOICEMAIL'].includes(t))                          return { label: 'messagerie', cls: 'vm' };
  if (['OUT_NO_ANSWER', 'CANCELLED', 'FAILED'].includes(t)) return { label: 'sans réponse', cls: 'muted' };
  return { label: (callType || '—').toLowerCase(), cls: 'muted' };
}

// Avatar collaborateur : initiales + couleur fournies par Ringover (sinon dérivées).
function agentAvatar(agentName, agentInitial, agentColor) {
  const initials = (agentInitial
    || (agentName || '?').split(/\s+/).map(w => w[0] || '').join('').slice(0, 2)
  ).toUpperCase();
  const color = agentColor && /^[0-9a-fA-F]{6}$/.test(agentColor) ? `#${agentColor}` : null;
  return { initials, color };
}

function formatHm(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

// Niveau de service (taux de décroché) → classe de santé commune.
function rateHealth(rate) {
  if (rate == null) return 'na';
  if (rate >= 80) return 'ok';
  if (rate >= 60) return 'warn';
  return 'ko';
}

const MOTIF_DIRECTIONS = [
  { id: '',         label: 'Entrants + sortants' },
  { id: 'inbound',  label: 'Entrants' },
  { id: 'outbound', label: 'Sortants' },
];

const MOTIF_COLORS = ['#0891b2', '#3b82f6', '#22c55e', '#f59e0b', '#a855f7', '#ec4899', '#14b8a6', '#ef4444', '#6366f1', '#84cc16'];

// ── Export CSV (séparateur ';' + BOM pour Excel FR avec accents) ─────────────
function downloadCsv(filename, headers, rows) {
  const escape = (v) => {
    const s = v == null ? '' : String(v);
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(';'), ...rows.map(r => r.map(escape).join(';'))];
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function computeRange(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case '7j':  { const d = new Date(today); d.setDate(d.getDate() - 6);  return { debut: toISODate(d), fin: toISODate(today) }; }
    case '30j': { const d = new Date(today); d.setDate(d.getDate() - 29); return { debut: toISODate(d), fin: toISODate(today) }; }
    case '90j': { const d = new Date(today); d.setDate(d.getDate() - 89); return { debut: toISODate(d), fin: toISODate(today) }; }
    case 'mois-en-cours': {
      const d = new Date(today.getFullYear(), today.getMonth(), 1);
      return { debut: toISODate(d), fin: toISODate(today) };
    }
    case 'mois-dernier': {
      const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const f = new Date(today.getFullYear(), today.getMonth(), 0);
      return { debut: toISODate(d), fin: toISODate(f) };
    }
    case 'trimestre': {
      const q = Math.floor(today.getMonth() / 3);
      const d = new Date(today.getFullYear(), q * 3, 1);
      return { debut: toISODate(d), fin: toISODate(today) };
    }
    case 'annee': {
      const d = new Date(today.getFullYear(), 0, 1);
      return { debut: toISODate(d), fin: toISODate(today) };
    }
    default: return { debut: null, fin: null };
  }
}

function formatInt(n) {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('fr-FR').format(n);
}

function formatDuration(seconds) {
  if (seconds == null || isNaN(seconds)) return '—';
  const s = Math.round(seconds);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return `${m} min ${String(r).padStart(2, '0')} s`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h} h ${String(mm).padStart(2, '0')}`;
}

function formatRelative(iso) {
  if (!iso) return 'jamais';
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return `il y a ${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.round(h / 24);
  return `il y a ${d} j`;
}

function formatDay(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

function sortArrow(sort, key) {
  if (sort.key !== key) return '';
  return sort.dir === 'asc' ? ' ▲' : ' ▼';
}

// ── Composant principal ─────────────────────────────────────────────────────

function RingoverReporting({ user, setHeaderAction }) {
  const [activeTab, setActiveTab] = useState('direct');
  const [preset, setPreset] = useState('30j');
  const [serviceCode, setServiceCode] = useState('');
  const [numero, setNumero] = useState('');
  const [numbers, setNumbers] = useState([]);
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState([]);
  const [byService, setByService] = useState([]);
  const [byStandard, setByStandard] = useState([]);
  const [stdSort, setStdSort] = useState({ key: 'duree_conv_total_s', dir: 'desc' });
  const [stdSearch, setStdSearch] = useState('');
  const [syncState, setSyncState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Onglet « Services & motifs »
  const [byScenario, setByScenario] = useState([]);
  const [byMotif, setByMotif] = useState([]);
  const [motifCoverage, setMotifCoverage] = useState(null);
  const [motifDirection, setMotifDirection] = useState('');
  const [loadingSvc, setLoadingSvc] = useState(false);
  const [refModal, setRefModal] = useState(null); // null | 'scenarios' | 'tags'

  const { debut, fin } = useMemo(() => computeRange(preset), [preset]);

  const isSuperAdmin = user?.role === 'super-admin';

  const loadAll = useCallback(async () => {
    if (!debut || !fin) return;
    setLoading(true);
    try {
      const params = { start: debut, end: fin };
      if (serviceCode && serviceCode !== 'NON_ATTRIBUE') params.service_code = serviceCode;
      if (numero) params.numero = numero;
      // Note : NON_ATTRIBUE n'est pas envoyé en filtre car la table contient NULL,
      // pas la chaîne. Filtrage géré côté frontend ou via une variante backend.
      // La concession est gérée globalement via le header X-Concession-Id (sélecteur
      // global du menu utilisateur) — pas de filtre dédié dans cette page.

      // by-service ne prend pas de filtre service (sinon il ne reste qu'une ligne).
      const byServiceParams = { start: debut, end: fin };
      if (numero) byServiceParams.numero = numero;

      // by-standard prend le filtre service mais pas le filtre numéro (il agrège
      // précisément par numéro de standard).
      const byStdParams = { start: debut, end: fin };
      if (serviceCode && serviceCode !== 'NON_ATTRIBUE') byStdParams.service_code = serviceCode;

      const [statsRes, seriesRes, byServiceRes, byStdRes, stateRes] = await Promise.all([
        api.get('/ringover-reporting/stats', { params }),
        api.get('/ringover-reporting/timeseries', { params: { ...params, granularity: 'day' } }),
        api.get('/ringover-reporting/by-service', { params: byServiceParams }),
        api.get('/ringover-reporting/by-standard', { params: byStdParams }),
        api.get('/ringover-reporting/sync-state'),
      ]);
      setStats(statsRes.data.stats);
      setSeries(seriesRes.data.series || []);
      setByService(byServiceRes.data.by_service || []);
      setByStandard(byStdRes.data.by_standard || []);
      setSyncState(stateRes.data);
    } catch (e) {
      console.error('Erreur chargement reporting Ringover:', e);
    } finally {
      setLoading(false);
    }
  }, [debut, fin, serviceCode, numero]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Charge la liste des numéros une fois au montage. Le backend applique
  // automatiquement le filtre concession effectif (header X-Concession-Id).
  useEffect(() => {
    api.get('/ringover-reporting/numbers-list')
      .then(r => setNumbers(r.data.numbers || []))
      .catch(() => setNumbers([]));
  }, []);

  // Données de l'onglet « Services & motifs » (chargées à la demande).
  const loadServices = useCallback(async () => {
    if (activeTab !== 'services' || !debut || !fin) return;
    setLoadingSvc(true);
    try {
      const motifParams = { start: debut, end: fin };
      if (motifDirection) motifParams.direction = motifDirection;
      const [scRes, moRes] = await Promise.all([
        api.get('/ringover-reporting/by-scenario', { params: { start: debut, end: fin } }),
        api.get('/ringover-reporting/by-motif', { params: motifParams }),
      ]);
      setByScenario(scRes.data.by_scenario || []);
      setByMotif(moRes.data.by_motif || []);
      setMotifCoverage(moRes.data.coverage || null);
    } catch (e) {
      console.error('Erreur chargement services & motifs Ringover:', e);
    } finally {
      setLoadingSvc(false);
    }
  }, [activeTab, debut, fin, motifDirection]);

  useEffect(() => { loadServices(); }, [loadServices]);

  const handleSyncNow = async () => {
    if (!isSuperAdmin) return;
    setSyncing(true);
    try {
      const res = await api.post('/ringover-reporting/sync-now');
      if (res.data.skipped) {
        alert(`Sync ignorée : ${res.data.skipped}`);
      } else {
        alert(`Synchronisation terminée : ${res.data.imported} appel(s) importé(s).`);
      }
      await loadAll();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleEnabled = async () => {
    if (!isSuperAdmin) return;
    try {
      await api.post('/ringover-reporting/toggle', { enabled: !syncState?.enabled });
      await loadAll();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur lors du basculement');
    }
  };

  // ── KPIs dérivés ──
  const inboundTotal    = parseInt(stats?.inbound_total || 0, 10);
  const inboundAnswered = parseInt(stats?.inbound_answered || 0, 10);
  const inboundMissed   = parseInt(stats?.inbound_missed || 0, 10);
  const answerRate      = inboundTotal > 0 ? (inboundAnswered / inboundTotal) * 100 : null;
  const avgDuration     = parseFloat(stats?.avg_duration_s || 0);
  const totalDuration   = parseInt(stats?.total_duration_s || 0, 10);
  const avgWait         = parseFloat(stats?.avg_wait_s || 0);

  // ── Série quotidienne ──
  const seriesPoints = useMemo(
    () => (series || []).map(p => ({
      bucket: p.bucket,
      label: formatDay(p.bucket),
      decroches: parseInt(p.inbound_answered || 0, 10),
      manques:   parseInt(p.inbound_missed || 0, 10),
    })),
    [series]
  );

  // ── Répartition par service ──
  const pieData = useMemo(
    () => (byService || []).map(s => ({
      name: s.service_nom,
      code: s.service_code,
      value: parseInt(s.inbound_total || 0, 10),
    })).filter(s => s.value > 0),
    [byService]
  );

  // ── Consommation par standard (onglet facturation) ──
  const standards = useMemo(() => (byStandard || []).map(s => {
    const entrants = parseInt(s.appels_entrants || 0, 10);
    const sortants = parseInt(s.appels_sortants || 0, 10);
    return {
      numero_clean: s.numero_clean,
      numero: s.numero,
      label: s.label,
      concession_id: parseInt(s.concession_id || 0, 10),
      concession_nom: s.concession_nom,
      service_code: s.service_code,
      service_nom: s.service_code ? (SERVICE_NOMS[s.service_code] || s.service_code) : '—',
      entrants,
      entrants_decroches: parseInt(s.entrants_decroches || 0, 10),
      entrants_manques: parseInt(s.entrants_manques || 0, 10),
      sortants,
      total: entrants + sortants,
      duree_entrants_s: parseInt(s.duree_conv_entrants_s || 0, 10),
      duree_sortants_s: parseInt(s.duree_conv_sortants_s || 0, 10),
      duree_total_s: parseInt(s.duree_conv_total_s || 0, 10),
    };
  }), [byStandard]);

  const standardsFiltered = useMemo(() => {
    const q = stdSearch.trim().toLowerCase();
    const filtered = q
      ? standards.filter(s =>
          (s.numero || '').toLowerCase().includes(q) ||
          (s.label || '').toLowerCase().includes(q) ||
          (s.concession_nom || '').toLowerCase().includes(q) ||
          (s.service_nom || '').toLowerCase().includes(q))
      : standards;
    const { key, dir } = stdSort;
    const mult = dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = a[key], vb = b[key];
      if (typeof va === 'string' || typeof vb === 'string') {
        return mult * String(va || '').localeCompare(String(vb || ''), 'fr');
      }
      return mult * ((va || 0) - (vb || 0));
    });
  }, [standards, stdSearch, stdSort]);

  // Rollup par concession (base de refacturation)
  const byConcession = useMemo(() => {
    const map = new Map();
    for (const s of standards) {
      const key = s.concession_id;
      if (!map.has(key)) {
        map.set(key, {
          concession_id: s.concession_id,
          concession_nom: s.concession_nom,
          entrants: 0, sortants: 0, duree_total_s: 0, nb_standards: 0,
        });
      }
      const c = map.get(key);
      c.entrants += s.entrants;
      c.sortants += s.sortants;
      c.duree_total_s += s.duree_total_s;
      c.nb_standards += 1;
    }
    return Array.from(map.values())
      .map(c => ({ ...c, total: c.entrants + c.sortants }))
      .sort((a, b) => b.duree_total_s - a.duree_total_s);
  }, [standards]);

  const consoKpis = useMemo(() => standards.reduce((acc, s) => {
    acc.entrants += s.entrants;
    acc.sortants += s.sortants;
    acc.duree_total_s += s.duree_total_s;
    return acc;
  }, { entrants: 0, sortants: 0, duree_total_s: 0, nb_standards: standards.length }), [standards]);

  const toggleStdSort = useCallback((key) => {
    setStdSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'desc' });
  }, []);

  // ── Export CSV (boutons dans le header, onglet conso uniquement) ──
  const exportConcessions = useCallback(() => {
    downloadCsv(
      `ringover_conso_concessions_${debut}_${fin}.csv`,
      ['Concession', 'Nb standards', 'Appels entrants', 'Appels sortants', 'Total appels', 'Temps conversation (s)', 'Temps conversation'],
      byConcession.map(c => [
        c.concession_nom, c.nb_standards, c.entrants, c.sortants, c.total, c.duree_total_s, formatDuration(c.duree_total_s),
      ])
    );
  }, [byConcession, debut, fin]);

  const exportStandards = useCallback(() => {
    downloadCsv(
      `ringover_conso_standards_${debut}_${fin}.csv`,
      ['Numero', 'Label', 'Concession', 'Service', 'Entrants', 'Decroches', 'Manques', 'Sortants', 'Total appels', 'Temps conv. entrants (s)', 'Temps conv. sortants (s)', 'Temps conv. total (s)', 'Temps conv. total'],
      standardsFiltered.map(s => [
        s.numero, s.label || '', s.concession_nom, s.service_code ? s.service_nom : '',
        s.entrants, s.entrants_decroches, s.entrants_manques, s.sortants, s.total,
        s.duree_entrants_s, s.duree_sortants_s, s.duree_total_s, formatDuration(s.duree_total_s),
      ])
    );
  }, [standardsFiltered, debut, fin]);

  // ── Services & motifs (dérivés) ──
  const scenarios = useMemo(() => (byScenario || []).map(s => ({
    service: s.service,
    total: parseInt(s.total || 0, 10),
    decroches: parseInt(s.decroches || 0, 10),
    manques: parseInt(s.manques || 0, 10),
  })), [byScenario]);
  const scenariosTotal = useMemo(() => scenarios.reduce((a, s) => a + s.total, 0), [scenarios]);

  const motifs = useMemo(() => (byMotif || []).map(m => ({
    motif: m.motif,
    nb_entrants: parseInt(m.nb_entrants || 0, 10),
    nb_sortants: parseInt(m.nb_sortants || 0, 10),
    total: parseInt(m.total || 0, 10),
  })), [byMotif]);

  const tagRate = useMemo(() => {
    const tot = parseInt(motifCoverage?.total_appels || 0, 10);
    if (!tot) return null;
    return (parseInt(motifCoverage.appels_tagges || 0, 10) / tot) * 100;
  }, [motifCoverage]);

  const exportScenarios = useCallback(() => {
    downloadCsv(
      `ringover_services_${debut}_${fin}.csv`,
      ['Service', 'Total entrants', 'Decroches', 'Manques'],
      scenarios.map(s => [s.service, s.total, s.decroches, s.manques])
    );
  }, [scenarios, debut, fin]);

  const exportMotifs = useCallback(() => {
    downloadCsv(
      `ringover_motifs_${debut}_${fin}.csv`,
      ['Motif', 'Entrants', 'Sortants', 'Total'],
      motifs.map(m => [m.motif, m.nb_entrants, m.nb_sortants, m.total])
    );
  }, [motifs, debut, fin]);

  useEffect(() => {
    if (!setHeaderAction) return;
    if (activeTab === 'conso') {
      setHeaderAction({
        secondary: [
          { label: 'Export concessions', onClick: exportConcessions },
          { label: 'Export standards', onClick: exportStandards },
        ],
      });
    } else if (activeTab === 'services') {
      const secondary = [
        { label: 'Export services', onClick: exportScenarios },
        { label: 'Export motifs', onClick: exportMotifs },
      ];
      if (isSuperAdmin) secondary.push({ label: '⚙️ Référentiel', onClick: () => setRefModal('scenarios') });
      setHeaderAction({ secondary });
    } else {
      setHeaderAction(null);
    }
    return () => { if (setHeaderAction) setHeaderAction(null); };
  }, [activeTab, setHeaderAction, exportConcessions, exportStandards, exportScenarios, exportMotifs, isSuperAdmin]);

  return (
    <div className="page-container">
      <div className="page-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            type="button"
            className={activeTab === t.id ? 'active' : ''}
            onClick={() => setActiveTab(t.id)}
          >{t.label}</button>
        ))}
      </div>

      {activeTab === 'direct' && <RingoverLive user={user} />}

      {activeTab !== 'direct' && (<>
      <div className="page-toolbar" style={{ flexWrap: 'wrap' }}>
        <div className="reporting-segment" role="group" aria-label="Période">
          {PRESETS.map(p => (
            <button
              key={p.id}
              type="button"
              className={preset === p.id ? 'active' : ''}
              onClick={() => setPreset(p.id)}
            >{p.label}</button>
          ))}
        </div>
        {activeTab !== 'services' && (
          <select
            className={`page-select${serviceCode ? ' active-filter' : ''}`}
            value={serviceCode}
            onChange={e => setServiceCode(e.target.value)}
            title="Filtrer par service"
          >
            {SERVICES.map(s => (
              <option key={s.code || '_all'} value={s.code}>{s.nom}</option>
            ))}
          </select>
        )}
        {activeTab === 'qualite' && (
          <select
            className={`page-select${numero ? ' active-filter' : ''}`}
            value={numero}
            onChange={e => setNumero(e.target.value)}
            title="Filtrer par numéro Ringover"
            style={{ maxWidth: 260 }}
          >
            <option value="">Tous les numéros</option>
            {numbers.map(n => {
              const cleanNum = (n.numero || '').replace(/[^0-9]/g, '');
              const display = n.label
                ? `${n.label} — ${n.numero}`
                : `${n.numero}${n.concession_nom ? ' · ' + n.concession_nom : ''}${!n.mapped ? ' (non mappé)' : ''}`;
              return <option key={cleanNum + '_' + (n.label || '')} value={cleanNum}>{display}</option>;
            })}
          </select>
        )}
        <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>
          Période : <strong>{debut}</strong> → <strong>{fin}</strong>
        </div>
      </div>

      {/* Bandeau sync */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
        background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-table)', flexWrap: 'wrap'
      }}>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', flex: 1 }}>
          {syncState?.has_api_key === false && (
            <span style={{ color: 'var(--color-warning)', fontWeight: 600 }}>
              ⚠️ Clé API Ringover non configurée (RINGOVER_API_KEY).
            </span>
          )}
          {syncState?.has_api_key && (
            <>
              <strong>Synchronisation :</strong>{' '}
              {syncState.enabled
                ? <span style={{ color: 'var(--color-success)' }}>active (toutes les 10 min)</span>
                : <span style={{ color: 'var(--text-muted)' }}>désactivée</span>}
              {' · '}
              <strong>Dernière sync :</strong> {formatRelative(syncState.state?.last_synced_at)}
              {' · '}
              <strong>État :</strong>{' '}
              <span style={{ color: syncState.state?.last_status === 'error' ? 'var(--color-error)' : 'var(--text-primary)' }}>
                {syncState.state?.last_status || '—'}
              </span>
              {syncState.state?.last_error && (
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--color-error)' }}>
                  Dernière erreur : {syncState.state.last_error}
                </div>
              )}
            </>
          )}
        </div>
        {isSuperAdmin && (
          <>
            <button
              className="btn-secondary"
              onClick={handleToggleEnabled}
              disabled={!syncState?.has_api_key}
              title={syncState?.enabled ? 'Désactiver la sync auto' : 'Activer la sync auto'}
            >
              {syncState?.enabled ? '⏸️ Désactiver' : '▶️ Activer'}
            </button>
            <button
              className="btn-primary"
              onClick={handleSyncNow}
              disabled={syncing || !syncState?.has_api_key}
            >
              {syncing ? 'Synchronisation…' : '🔄 Synchroniser maintenant'}
            </button>
          </>
        )}
      </div>
      </>)}

      {activeTab === 'qualite' && (<>
      {/* KPI cards */}
      <div className="reporting-kpi-grid">
        <div className="reporting-kpi-card">
          <div className="label">Appels entrants</div>
          <div className="value">{formatInt(inboundTotal)}</div>
          <div className="sub">sur la période</div>
        </div>
        <div className="reporting-kpi-card">
          <div className="label">Taux de décroché</div>
          <div className="value">
            {answerRate == null ? '—' : `${answerRate.toFixed(1).replace('.', ',')} %`}
          </div>
          <div className="sub">{formatInt(inboundAnswered)} décroché(s)</div>
        </div>
        <div className="reporting-kpi-card">
          <div className="label">Appels manqués</div>
          <div className="value" style={{ color: inboundMissed > 0 ? 'var(--color-error)' : undefined }}>
            {formatInt(inboundMissed)}
          </div>
          <div className="sub">
            {inboundTotal > 0
              ? `${((inboundMissed / inboundTotal) * 100).toFixed(1).replace('.', ',')} % du total`
              : '—'}
          </div>
        </div>
        <div className="reporting-kpi-card">
          <div className="label">Temps d'attente moyen</div>
          <div className="value">{formatDuration(avgWait)}</div>
          <div className="sub">avant décroché</div>
        </div>
        <div className="reporting-kpi-card">
          <div className="label">Durée moyenne</div>
          <div className="value">{formatDuration(avgDuration)}</div>
          <div className="sub">par appel décroché</div>
        </div>
        <div className="reporting-kpi-card">
          <div className="label">Durée totale</div>
          <div className="value">{formatDuration(totalDuration)}</div>
          <div className="sub">cumul des appels</div>
        </div>
      </div>

      {/* Graphique 1 : évolution journalière */}
      <div className="reporting-chart-card">
        <h3>📈 Évolution journalière — décrochés vs manqués</h3>
        {loading ? (
          <div className="reporting-empty">Chargement…</div>
        ) : seriesPoints.length === 0 ? (
          <div className="reporting-empty">Aucun appel sur cette période.</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={seriesPoints} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} allowDecimals={false} />
              <Tooltip
                cursor={{ fill: 'var(--bg-hover)' }}
                content={({ active, payload, label }) => {
                  if (!active || !payload || !payload.length) return null;
                  return (
                    <div className="reporting-tooltip">
                      <div className="tooltip-title">{label}</div>
                      {payload.map(p => (
                        <div className="tooltip-row" key={p.dataKey}>
                          <span><span className="swatch" style={{ background: p.color }} />{p.name}</span>
                          <span>{formatInt(p.value)}</span>
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="decroches" name="Décrochés" stackId="a" fill="#22c55e" radius={[0, 0, 0, 0]} />
              <Bar dataKey="manques"   name="Manqués"   stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Graphique 2 : répartition par service */}
      <div className="reporting-chart-card">
        <h3>🥧 Répartition des appels entrants par service</h3>
        {loading ? (
          <div className="reporting-empty">Chargement…</div>
        ) : pieData.length === 0 ? (
          <div className="reporting-empty">Aucun appel sur cette période.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'center' }}>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  innerRadius={50}
                  paddingAngle={2}
                >
                  {pieData.map(entry => (
                    <Cell key={entry.code} fill={SERVICE_COLORS[entry.code] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload || !payload.length) return null;
                    const p = payload[0].payload;
                    const total = pieData.reduce((s, d) => s + d.value, 0);
                    const pct = total > 0 ? (p.value / total) * 100 : 0;
                    return (
                      <div className="reporting-tooltip">
                        <div className="tooltip-title">{p.name}</div>
                        <div className="tooltip-row"><span>Appels</span><span>{formatInt(p.value)}</span></div>
                        <div className="tooltip-row"><span>Part</span><span>{pct.toFixed(1).replace('.', ',')} %</span></div>
                      </div>
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {byService.map(s => {
                const total = parseInt(s.inbound_total || 0, 10);
                const answered = parseInt(s.inbound_answered || 0, 10);
                const missed = parseInt(s.inbound_missed || 0, 10);
                const rate = total > 0 ? (answered / total) * 100 : null;
                return (
                  <div
                    key={s.service_code}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', background: 'var(--bg-muted)',
                      borderRadius: 8, fontSize: 13
                    }}
                  >
                    <span style={{
                      width: 12, height: 12, borderRadius: 3,
                      background: SERVICE_COLORS[s.service_code] || '#94a3b8'
                    }} />
                    <span style={{ flex: 1, fontWeight: 600 }}>{s.service_nom}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {formatInt(total)} appels · {rate != null ? `${rate.toFixed(0)}% décr.` : '—'} · {formatInt(missed)} manqué(s)
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      </>)}

      {activeTab === 'conso' && (<>
        {/* Note méthodo facturation */}
        <div className="rr-conso-note">
          💡 Base de refacturation : un <strong>standard</strong> = un numéro de ligne Ringover.
          Le temps facturé est le <strong>temps de conversation</strong> (décroché → raccroché).
          Les standards rattachés à <em>« Non attribué »</em> ne sont pas encore mappés à une
          concession — complétez le mapping dans <strong>Référentiels → Numéros Ringover</strong>
          pour les imputer.
        </div>

        {/* KPI conso */}
        <div className="reporting-kpi-grid">
          <div className="reporting-kpi-card">
            <div className="label">Appels entrants</div>
            <div className="value">{formatInt(consoKpis.entrants)}</div>
            <div className="sub">reçus sur la période</div>
          </div>
          <div className="reporting-kpi-card">
            <div className="label">Appels sortants</div>
            <div className="value">{formatInt(consoKpis.sortants)}</div>
            <div className="sub">émis sur la période</div>
          </div>
          <div className="reporting-kpi-card">
            <div className="label">Temps de conversation</div>
            <div className="value">{formatDuration(consoKpis.duree_total_s)}</div>
            <div className="sub">cumul entrants + sortants</div>
          </div>
          <div className="reporting-kpi-card">
            <div className="label">Standards actifs</div>
            <div className="value">{formatInt(consoKpis.nb_standards)}</div>
            <div className="sub">numéros avec trafic</div>
          </div>
        </div>

        {/* Synthèse par concession (base de facturation) */}
        <div className="reporting-chart-card">
          <h3>🏢 Synthèse par concession</h3>
          {loading ? (
            <div className="reporting-empty">Chargement…</div>
          ) : byConcession.length === 0 ? (
            <div className="reporting-empty">Aucun appel sur cette période.</div>
          ) : (
            <div className="table-container">
              <table className="rr-conso-table">
                <thead>
                  <tr>
                    <th>Concession</th>
                    <th className="num">Standards</th>
                    <th className="num">Entrants</th>
                    <th className="num">Sortants</th>
                    <th className="num">Total appels</th>
                    <th className="num">Temps conversation</th>
                  </tr>
                </thead>
                <tbody>
                  {byConcession.map(c => (
                    <tr key={c.concession_id}>
                      <td className={c.concession_id === 0 ? 'rr-non-attribue' : ''}>{c.concession_nom}</td>
                      <td className="num">{formatInt(c.nb_standards)}</td>
                      <td className="num">{formatInt(c.entrants)}</td>
                      <td className="num">{formatInt(c.sortants)}</td>
                      <td className="num"><strong>{formatInt(c.total)}</strong></td>
                      <td className="num">{formatDuration(c.duree_total_s)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td className="num">{formatInt(consoKpis.nb_standards)}</td>
                    <td className="num">{formatInt(consoKpis.entrants)}</td>
                    <td className="num">{formatInt(consoKpis.sortants)}</td>
                    <td className="num"><strong>{formatInt(consoKpis.entrants + consoKpis.sortants)}</strong></td>
                    <td className="num">{formatDuration(consoKpis.duree_total_s)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Détail par standard */}
        <div className="reporting-chart-card">
          <div className="rr-conso-head">
            <h3>📞 Détail par standard</h3>
            <input
              className="page-search"
              placeholder="🔍 Rechercher…"
              value={stdSearch}
              onChange={e => setStdSearch(e.target.value)}
            />
          </div>
          {loading ? (
            <div className="reporting-empty">Chargement…</div>
          ) : standardsFiltered.length === 0 ? (
            <div className="reporting-empty">Aucun standard sur cette période.</div>
          ) : (
            <div className="table-container">
              <table className="rr-conso-table">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => toggleStdSort('numero')}>Numéro{sortArrow(stdSort, 'numero')}</th>
                    <th>Label</th>
                    <th className="sortable" onClick={() => toggleStdSort('concession_nom')}>Concession{sortArrow(stdSort, 'concession_nom')}</th>
                    <th>Service</th>
                    <th className="num sortable" onClick={() => toggleStdSort('entrants')}>Entrants{sortArrow(stdSort, 'entrants')}</th>
                    <th className="num">Décr. / Manq.</th>
                    <th className="num sortable" onClick={() => toggleStdSort('sortants')}>Sortants{sortArrow(stdSort, 'sortants')}</th>
                    <th className="num sortable" onClick={() => toggleStdSort('total')}>Total{sortArrow(stdSort, 'total')}</th>
                    <th className="num sortable" onClick={() => toggleStdSort('duree_total_s')}>Temps conv.{sortArrow(stdSort, 'duree_total_s')}</th>
                  </tr>
                </thead>
                <tbody>
                  {standardsFiltered.map(s => (
                    <tr key={s.numero_clean}>
                      <td>{s.numero}</td>
                      <td>{s.label || <span className="rr-muted">—</span>}</td>
                      <td className={s.concession_id === 0 ? 'rr-non-attribue' : ''}>{s.concession_nom}</td>
                      <td>{s.service_code ? s.service_nom : <span className="rr-muted">—</span>}</td>
                      <td className="num">{formatInt(s.entrants)}</td>
                      <td className="num rr-muted">{formatInt(s.entrants_decroches)} / {formatInt(s.entrants_manques)}</td>
                      <td className="num">{formatInt(s.sortants)}</td>
                      <td className="num"><strong>{formatInt(s.total)}</strong></td>
                      <td className="num">{formatDuration(s.duree_total_s)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>)}

      {/* ───────────────────────── SERVICES & MOTIFS ───────────────────────── */}
      {activeTab === 'services' && (<>
        <div className="rr-conso-note">
          🧭 Analyse fine des appels. <strong>Services (entrants)</strong> : déduits de la touche
          choisie au standard (SVI Ringover) — normalisables via <strong>⚙️ Référentiel</strong>.
          <strong> Motifs</strong> : tags Ringover (entrants + sortants) ; la couverture dépend du
          taggage par les équipes (le taux est indiqué, les appels non taggés ne sont pas répartis).
          Le filtre <strong>concession</strong> est le sélecteur global en haut de l'application.
        </div>

        {/* Répartition par service (entrants) */}
        <div className="reporting-chart-card">
          <h3>🧭 Répartition des appels entrants par service</h3>
          {loadingSvc ? (
            <div className="reporting-empty">Chargement…</div>
          ) : scenarios.length === 0 ? (
            <div className="reporting-empty">Aucun appel entrant sur cette période.</div>
          ) : (
            <div className="rr-svc-grid">
              <ResponsiveContainer width="100%" height={Math.max(180, Math.min(scenarios.length, 12) * 30)}>
                <BarChart data={scenarios.slice(0, 12)} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} allowDecimals={false} />
                  <YAxis
                    type="category" dataKey="service" width={150}
                    tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                    tickFormatter={(v) => (v.length > 22 ? v.slice(0, 20) + '…' : v)}
                  />
                  <Tooltip
                    cursor={{ fill: 'var(--bg-hover)' }}
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      return (
                        <div className="reporting-tooltip">
                          <div className="tooltip-title">{payload[0].payload.service}</div>
                          <div className="tooltip-row"><span>Appels</span><span>{formatInt(payload[0].value)}</span></div>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                    {scenarios.slice(0, 12).map((e, i) => <Cell key={e.service} fill={MOTIF_COLORS[i % MOTIF_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="table-container">
                <table className="rr-conso-table">
                  <thead>
                    <tr><th>Service</th><th className="num">Appels</th><th className="num">Décr.</th><th className="num">Manq.</th><th className="num">Part</th></tr>
                  </thead>
                  <tbody>
                    {scenarios.map(s => (
                      <tr key={s.service}>
                        <td>{s.service}</td>
                        <td className="num">{formatInt(s.total)}</td>
                        <td className="num">{formatInt(s.decroches)}</td>
                        <td className="num">{formatInt(s.manques)}</td>
                        <td className="num">{scenariosTotal > 0 ? `${Math.round((s.total / scenariosTotal) * 100)} %` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Motifs d'appels (tags) */}
        <div className="reporting-chart-card">
          <div className="rr-conso-head">
            <h3>🏷️ Motifs d'appels</h3>
            <select
              className="page-select"
              value={motifDirection}
              onChange={e => setMotifDirection(e.target.value)}
              title="Sens des appels"
              style={{ maxWidth: 200 }}
            >
              {MOTIF_DIRECTIONS.map(d => <option key={d.id || 'all'} value={d.id}>{d.label}</option>)}
            </select>
          </div>
          {motifCoverage && (
            <div className="rr-conso-note" style={{ marginBottom: 12 }}>
              Taux de taggage : <strong>{tagRate == null ? '—' : `${tagRate.toFixed(1).replace('.', ',')} %`}</strong>
              {' '}({formatInt(motifCoverage.appels_tagges)} appels taggés sur {formatInt(motifCoverage.total_appels)}).
              Les appels non taggés ne sont pas répartis par motif.
            </div>
          )}
          {loadingSvc ? (
            <div className="reporting-empty">Chargement…</div>
          ) : motifs.length === 0 ? (
            <div className="reporting-empty">Aucun appel taggé sur cette période.</div>
          ) : (
            <div className="table-container">
              <table className="rr-conso-table">
                <thead>
                  <tr><th>Motif</th><th className="num">Entrants</th><th className="num">Sortants</th><th className="num">Total</th></tr>
                </thead>
                <tbody>
                  {motifs.map(m => (
                    <tr key={m.motif}>
                      <td>{m.motif}</td>
                      <td className="num">{formatInt(m.nb_entrants)}</td>
                      <td className="num">{formatInt(m.nb_sortants)}</td>
                      <td className="num"><strong>{formatInt(m.total)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>)}

      {refModal && (
        <ServiceRefModal
          kind={refModal}
          onSwitch={setRefModal}
          onClose={() => setRefModal(null)}
          onSaved={() => loadServices()}
        />
      )}
    </div>
  );
}

// ── Vue « Direct » : wallboard temps réel du plateau ─────────────────────────
// Portée = aujourd'hui (00:00 → maintenant). Auto-rafraîchissement toutes les
// 30 s (en pause si l'onglet navigateur est masqué) + flux d'appels entrants
// poussé en direct via SSE (même canal que LeadsServices). La concession est
// appliquée globalement par le header X-Concession-Id (sélecteur du menu).
const REFRESH_MS = 30000;

// Jauge « compteur de vitesse » d'un standard : arc semi-circulaire composé de
// deux segments (décroché + rappelé) sur fond gris (non traité). Le total
// coloré = taux de service.
function SpeedoGauge({ entrants, decroches, recuperes, label, concession, unit = 'reçus' }) {
  const d = entrants > 0 ? decroches / entrants : 0;
  const r = entrants > 0 ? recuperes / entrants : 0;
  const service = Math.round((d + r) * 100);
  const R = 46, sw = 11;
  const W = 2 * R + sw, cy = R + sw / 2;
  const L = Math.PI * R;
  const path = `M ${sw / 2} ${cy} A ${R} ${R} 0 0 1 ${W - sw / 2} ${cy}`;
  return (
    <div className="rr-spd">
      <div className="rr-spd-arc">
        <svg viewBox={`0 0 ${W} ${cy + 2}`} className="rr-spd-svg" role="img" aria-label={`${service}% de service`}>
          <path d={path} fill="none" stroke="var(--bg-muted)" strokeWidth={sw} />
          <path d={path} fill="none" stroke="var(--color-success)" strokeWidth={sw} strokeDasharray={`${d * L} ${L}`} />
          <path d={path} fill="none" stroke="var(--color-accent)" strokeWidth={sw} strokeDasharray={`${r * L} ${L}`} strokeDashoffset={`${-d * L}`} />
        </svg>
        <span className="rr-spd-val">{entrants > 0 ? `${service}%` : '—'}</span>
      </div>
      <div className="rr-spd-name" title={label}>{label}</div>
      <div className="rr-spd-sub">{formatInt(entrants)} {unit}{concession ? ` · ${concession}` : ''}</div>
    </div>
  );
}

function RingoverLive({ user }) {
  const [today, setToday] = useState(null);
  const [agents, setAgents] = useState([]);
  const [stdRows, setStdRows] = useState([]); // détail (standard × service SVI)
  const [serviceLevel, setServiceLevel] = useState([]); // jauges par standard
  const [feed, setFeed] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [syncedAt, setSyncedAt] = useState(null);
  const [connected, setConnected] = useState(false);
  const [sseBump, setSseBump] = useState(0);
  const [agentSort, setAgentSort] = useState({ key: 'decroches', dir: 'desc' });
  const [agentStds, setAgentStds] = useState({}); // agent_user_id → [standards]
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [openStds, setOpenStds] = useState(() => new Set()); // standards dépliés (détail SVI)
  const [gaugeMode, setGaugeMode] = useState('calls'); // 'calls' | 'callers'
  const [, setTick] = useState(0); // force le recalcul des « il y a X »

  const loadLive = useCallback(async () => {
    const day = toISODate(new Date());
    const params = { start: day, end: day };
    try {
      const [agRes, sdRes, fdRes, tdRes, asRes, slRes] = await Promise.all([
        api.get('/ringover-reporting/by-agent', { params }),
        api.get('/ringover-reporting/live/standards-detail', { params }),
        api.get('/ringover-reporting/live/feed', { params: { ...params, limit: 40 } }),
        api.get('/ringover-reporting/live/today', { params }),
        api.get('/ringover-reporting/live/agent-standards', { params }),
        api.get('/ringover-reporting/live/service-level', { params }),
      ]);
      setServiceLevel(slRes.data.service_level || []);
      const asMap = {};
      (asRes.data.agent_standards || []).forEach(r => {
        const k = String(r.agent_user_id);
        (asMap[k] = asMap[k] || []).push(r);
      });
      setAgentStds(asMap);
      setAgents(agRes.data.by_agent || []);
      setStdRows(sdRes.data.standards_detail || []);
      setFeed(fdRes.data.feed || []);
      setToday(tdRes.data.today || null);
      setSyncedAt(new Date());
      setSseBump(0); // les arrivées SSE sont désormais réconciliées par le CDR
    } catch (e) {
      console.error('Erreur chargement Direct Ringover:', e);
    } finally {
      setFirstLoad(false);
    }
  }, []);

  // Chargement initial + auto-refresh (en pause si onglet masqué).
  useEffect(() => {
    loadLive();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') loadLive();
    }, REFRESH_MS);
    const onVis = () => { if (document.visibilityState === 'visible') loadLive(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [loadLive]);

  // Horloge légère pour rafraîchir l'affichage des durées relatives.
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 15000);
    return () => clearInterval(id);
  }, []);

  // Flux SSE des appels entrants (instantané, avant même la prochaine sync).
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const apiBase = process.env.REACT_APP_API_URL || '';
    const es = new EventSource(`${apiBase}/api/webhooks/ringover/events?token=${token}`);
    es.onopen = () => setConnected(true);
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type !== 'incoming_call') return;
        // Super-admin : seuls les numéros explicitement visibles (cf. LeadsServices).
        if (user?.role === 'super-admin' && !data.visible_superadmin) return;
        const client = `${data.client_prenom || ''} ${data.client_nom || ''}`.trim();
        setFeed(prev => [{
          _live: true,
          _key: `sse-${Date.now()}-${Math.round(Math.random() * 1e6)}`,
          start_time: new Date().toISOString(),
          direction: 'inbound',
          call_type: 'RINGING',
          from_number: data.from_number,
          to_number: data.to_number,
          numero_label: data.concession_nom || null,
          service_code: null,
          client_label: client || null,
          agent_name: null, agent_initial: null, agent_color: null,
          ringing_duration_seconds: 0, in_call_duration_seconds: 0,
        }, ...prev].slice(0, 60));
        setSseBump(n => n + 1);
      } catch (_) { /* ignore */ }
    };
    es.onerror = () => setConnected(false);
    return () => { es.close(); setConnected(false); };
  }, [user]);

  // ── Indicateurs (tous scopés via le mapping, cohérents avec les panneaux) ──
  const inboundTotal    = parseInt(today?.inbound_total || 0, 10);
  const inboundAnswered = parseInt(today?.inbound_answered || 0, 10);
  const inboundRecup    = parseInt(today?.inbound_recuperes || 0, 10);
  // Taux de service = (décrochés + manqués récupérés) / entrants.
  const serviceRate     = inboundTotal > 0 ? ((inboundAnswered + inboundRecup) / inboundTotal) * 100 : null;
  const avgWait         = parseFloat(today?.avg_wait_s || 0);
  const avgDuration     = parseFloat(today?.avg_duration_s || 0);
  const manques         = today?.manques_a_traiter ?? null;

  const agentsSorted = useMemo(() => {
    const arr = [...agents];
    const { key, dir } = agentSort;
    arr.sort((a, b) => {
      const va = parseFloat(a[key] || 0), vb = parseFloat(b[key] || 0);
      return dir === 'asc' ? va - vb : vb - va;
    });
    return arr;
  }, [agents, agentSort]);
  const maxTraites = useMemo(
    () => Math.max(1, ...agents.map(a => parseInt(a.traites || 0, 10))),
    [agents]
  );

  // Niveau standard (clients uniques) indexé par libellé, pour les en-têtes du panneau.
  const slByLabel = useMemo(() => {
    const m = {};
    for (const s of serviceLevel) m[s.numero_label || s.numero] = s;
    return m;
  }, [serviceLevel]);

  // Regroupe le détail (standard × service SVI) par standard, en CLIENTS UNIQUES.
  // Les compteurs au niveau standard viennent de service-level (déjà dédoublonnés
  // au niveau standard) ; les services sont triés par « à rappeler » décroissant.
  const standardsGrouped = useMemo(() => {
    const map = new Map();
    for (const r of stdRows) {
      const key = r.numero_label || r.numero;
      if (!map.has(key)) {
        map.set(key, { key, label: r.numero_label || r.numero, concession_nom: r.concession_nom, services: [] });
      }
      map.get(key).services.push({
        service: r.service,
        clients: parseInt(r.clients || 0, 10),
        dec: parseInt(r.clients_dec || 0, 10),
        rec: parseInt(r.clients_rec || 0, 10),
        todo: parseInt(r.clients_todo || 0, 10),
      });
    }
    const arr = [...map.values()];
    arr.forEach(g => {
      const sl = slByLabel[g.key];
      g.clients   = sl ? parseInt(sl.callers_total || 0, 10) : g.services.reduce((a, s) => a + s.clients, 0);
      g.aRappeler = sl ? parseInt(sl.callers_todo || 0, 10)  : g.services.reduce((a, s) => a + s.todo, 0);
      const servis = sl ? (parseInt(sl.callers_dec || 0, 10) + parseInt(sl.callers_rec || 0, 10)) : (g.clients - g.aRappeler);
      g.rate = g.clients > 0 ? (servis / g.clients) * 100 : null;
      g.services.sort((a, b) => b.todo - a.todo || b.clients - a.clients);
    });
    return arr.filter(g => g.clients > 0).sort((a, b) => b.clients - a.clients);
  }, [stdRows, slByLabel]);
  const maxStdClients = useMemo(
    () => Math.max(1, ...standardsGrouped.map(g => g.clients)),
    [standardsGrouped]
  );

  const toggleAgentSort = (key) => setAgentSort(s =>
    s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
  );

  const todayLabel = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  if (firstLoad) {
    return <div className="rr-live-loading">Chargement du plateau…</div>;
  }

  const serviceRateClass = rateHealth(serviceRate);

  return (
    <div className="rr-live">
      {/* Barre d'état temps réel */}
      <div className="rr-live-bar">
        <div className="rr-live-status">
          <span className={`rr-live-dot ${connected ? 'on' : 'off'}`} />
          <strong>En direct</strong>
          <span className="rr-live-sep">·</span>
          <span className="rr-live-muted">
            {connected ? 'flux connecté' : 'flux hors-ligne'}
            {syncedAt && <> · actualisé {formatRelative(syncedAt.toISOString())}</>}
          </span>
        </div>
        <div className="rr-live-day">
          {today?.last_call_at && <span className="rr-live-muted">dernier appel {formatRelative(today.last_call_at)} · </span>}
          <span className="rr-live-today">Aujourd’hui · {todayLabel}</span>
        </div>
      </div>

      {/* Bande de compteurs */}
      <div className="rr-live-counters">
        <div className="rr-live-counter">
          <div className="rr-lc-val">
            {formatInt(inboundTotal)}
            {sseBump > 0 && <span className="rr-lc-bump">+{sseBump}</span>}
          </div>
          <div className="rr-lc-lbl">Appels reçus</div>
        </div>
        <div className="rr-live-counter">
          <div className={`rr-lc-val ${serviceRateClass}`}>
            {serviceRate == null ? '—' : `${serviceRate.toFixed(0)} %`}
          </div>
          <div className="rr-lc-lbl">Taux de service</div>
        </div>
        <div className="rr-live-counter">
          <div className={`rr-lc-val ${manques > 0 ? 'ko' : ''}`}>{manques == null ? '—' : formatInt(manques)}</div>
          <div className="rr-lc-lbl">Clients à rappeler</div>
        </div>
        <div className="rr-live-counter">
          <div className="rr-lc-val">{avgWait > 0 ? formatDuration(avgWait) : '—'}</div>
          <div className="rr-lc-lbl">Attente moyenne</div>
        </div>
        <div className="rr-live-counter">
          <div className="rr-lc-val">{avgDuration > 0 ? formatDuration(avgDuration) : '—'}</div>
          <div className="rr-lc-lbl">Durée moyenne</div>
        </div>
      </div>

      {/* Jauges de taux de service par standard */}
      {serviceLevel.length > 0 && (
        <section className="rr-live-panel rr-live-spds">
          <header className="rr-live-ph">
            <h3>Taux de service par standard</h3>
            <div className="rr-spd-controls">
              <div className="rr-spd-legend">
                <span><i className="rr-spd-dot dec" />décroché</span>
                <span><i className="rr-spd-dot rec" />rappelé</span>
                <span><i className="rr-spd-dot todo" />non traité</span>
              </div>
              <div className="rr-spd-toggle" role="group" aria-label="Unité">
                <button className={gaugeMode === 'calls' ? 'on' : ''} onClick={() => setGaugeMode('calls')}>Appels</button>
                <button className={gaugeMode === 'callers' ? 'on' : ''} onClick={() => setGaugeMode('callers')} title="Dédoublonne les appels d'un même numéro">Appelants uniques</button>
              </div>
            </div>
          </header>
          <div className="rr-spd-row">
            {serviceLevel.map((s, i) => {
              const callers = gaugeMode === 'callers';
              return (
                <SpeedoGauge
                  key={s.numero || i}
                  entrants={parseInt((callers ? s.callers_total : s.entrants) || 0, 10)}
                  decroches={parseInt((callers ? s.callers_dec : s.decroches) || 0, 10)}
                  recuperes={parseInt((callers ? s.callers_rec : s.recuperes) || 0, 10)}
                  label={s.numero_label || s.numero || 'Standard'}
                  concession={s.concession_nom}
                  unit={callers ? 'appelants' : 'reçus'}
                />
              );
            })}
          </div>
        </section>
      )}

      {/* Corps : collaborateurs (focal) + standards (latéral) */}
      <div className="rr-live-grid">
        <section className="rr-live-panel rr-live-agents">
          <header className="rr-live-ph">
            <h3>Collaborateurs</h3>
            <span className="rr-live-muted">{agents.length} actif·s aujourd’hui</span>
          </header>
          {agentsSorted.length === 0 ? (
            <div className="rr-live-empty">Aucun appel traité pour l’instant.</div>
          ) : (
            <div className="rr-agt-table">
              <div className="rr-agt-head">
                <span>Collaborateur</span>
                <button className={agentSort.key === 'decroches' ? 'on' : ''} onClick={() => toggleAgentSort('decroches')}>Décrochés{sortArrow(agentSort, 'decroches')}</button>
                <button className={agentSort.key === 'sortants' ? 'on' : ''} onClick={() => toggleAgentSort('sortants')}>Sortants{sortArrow(agentSort, 'sortants')}</button>
                <button className={agentSort.key === 'duree_conv_total_s' ? 'on' : ''} onClick={() => toggleAgentSort('duree_conv_total_s')}>En ligne{sortArrow(agentSort, 'duree_conv_total_s')}</button>
                <button className={agentSort.key === 'duree_moy_s' ? 'on' : ''} onClick={() => toggleAgentSort('duree_moy_s')}>Moy.{sortArrow(agentSort, 'duree_moy_s')}</button>
              </div>
              {agentsSorted.map((a, i) => {
                const { color } = agentAvatar(a.agent_name, a.agent_initial, a.agent_color);
                const traites = parseInt(a.traites || 0, 10);
                const pct = Math.round((traites / maxTraites) * 100);
                const aid = String(a.agent_user_id);
                const isExp = expandedAgent === aid;
                const stds = agentStds[aid] || [];
                const maxStd = Math.max(1, ...stds.map(s => parseInt(s.appels || 0, 10)));
                return (
                  <React.Fragment key={a.agent_user_id || i}>
                    <div
                      className={`rr-agt-row rr-agt-clic ${isExp ? 'open' : ''}`}
                      onClick={() => setExpandedAgent(isExp ? null : aid)}
                      title="Voir la répartition par standard"
                    >
                      <div className="rr-agt-id">
                        <span className="rr-agt-rank">{i + 1}</span>
                        <div className="rr-agt-name">
                          <span className="rr-agt-nm"><span className="rr-agt-caret">{isExp ? '▾' : '▸'}</span>{a.agent_name || 'Inconnu'}</span>
                          <span className="rr-agt-bar"><i style={{ width: `${pct}%`, background: color || 'var(--color-accent)' }} /></span>
                        </div>
                      </div>
                      <span className="rr-agt-num rr-strong">{formatInt(a.decroches)}</span>
                      <span className="rr-agt-num">{formatInt(a.sortants)}</span>
                      <span className="rr-agt-num">{formatDuration(parseInt(a.duree_conv_total_s || 0, 10))}</span>
                      <span className="rr-agt-num rr-live-muted">{formatDuration(parseFloat(a.duree_moy_s || 0))}</span>
                    </div>
                    {isExp && (
                      <div className="rr-agt-detail">
                        {stds.length === 0 ? (
                          <div className="rr-agt-detail-empty">Aucun appel sur un standard configuré aujourd’hui.</div>
                        ) : stds.map((s, j) => {
                          const n = parseInt(s.appels || 0, 10);
                          const w = Math.round((n / maxStd) * 100);
                          return (
                            <div className="rr-agt-std" key={j}>
                              <span className="rr-agt-std-name">
                                {s.numero_label || s.numero || 'Standard'}
                                {s.concession_nom && <span className="rr-live-muted"> · {s.concession_nom}</span>}
                              </span>
                              <span className="rr-agt-std-bar"><i style={{ width: `${w}%`, background: color || 'var(--color-accent)' }} /></span>
                              <span className="rr-agt-std-n">{formatInt(n)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </section>

        <section className="rr-live-panel rr-live-stds">
          <header className="rr-live-ph">
            <h3>Standards &amp; services</h3>
            <span className="rr-live-muted">clients uniques · à rappeler</span>
          </header>
          {standardsGrouped.length === 0 ? (
            <div className="rr-live-empty">Aucun trafic aujourd’hui.</div>
          ) : (
            <div className="rr-sd-list">
              {standardsGrouped.map((g) => {
                const volPct = Math.round((g.clients / maxStdClients) * 100);
                const isOpen = openStds.has(g.key);
                const toggle = () => setOpenStds(prev => {
                  const n = new Set(prev);
                  if (n.has(g.key)) n.delete(g.key); else n.add(g.key);
                  return n;
                });
                return (
                  <div className={`rr-sd ${isOpen ? 'open' : ''}`} key={g.key}>
                    <div className="rr-sd-head rr-sd-clic" onClick={toggle} title="Afficher / masquer le détail par service">
                      <div className="rr-sd-id">
                        <span className="rr-sd-caret">{isOpen ? '▾' : '▸'}</span>
                        <span className="rr-sd-name">{g.label}</span>
                        {g.concession_nom && <span className="rr-live-muted rr-sd-conc">{g.concession_nom}</span>}
                      </div>
                    </div>
                    <div className="rr-sd-meta">
                      <span className="rr-sd-vol">
                        <span className="rr-sd-volbar"><i style={{ width: `${volPct}%` }} /></span>
                        <span><b>{formatInt(g.clients)}</b> clients</span>
                      </span>
                      {g.aRappeler > 0 && <span className="rr-sd-miss">{formatInt(g.aRappeler)} à rappeler</span>}
                    </div>
                    {isOpen && (
                      <div className="rr-sd-svcs">
                        {g.services.map((s, j) => {
                          const sr = s.clients > 0 ? ((s.dec + s.rec) / s.clients) * 100 : null;
                          const sh = rateHealth(sr);
                          return (
                            <div className="rr-svc-line" key={j}>
                              <span className="rr-svc-nm" title={s.service}>{s.service}</span>
                              <span className={`rr-gauge sm ${sh}`}><i style={{ width: `${sr || 0}%` }} /></span>
                              <span className="rr-svc-rec" title="clients">{formatInt(s.clients)}</span>
                              <span className="rr-svc-miss" title="à rappeler">{s.todo > 0 ? formatInt(s.todo) : ''}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Flux d'appels */}
      <section className="rr-live-panel rr-live-feed">
        <header className="rr-live-ph">
          <h3>Flux d’appels</h3>
          <span className="rr-live-muted">temps réel · aujourd’hui</span>
        </header>
        {feed.length === 0 ? (
          <div className="rr-live-empty">Aucun appel aujourd’hui.</div>
        ) : (
          <div className="rr-feed-list">
            {feed.map((f, i) => {
              const meta = callStatusMeta(f.direction, f.call_type);
              const inbound = f.direction === 'inbound';
              const std = f.numero_label || (inbound ? f.to_number : f.from_number) || '—';
              const other = inbound ? f.from_number : f.to_number;
              const dur = parseInt(f.in_call_duration_seconds || 0, 10);
              return (
                <div className={`rr-feed-row ${f._live ? 'live' : ''}`} key={f._key || `${f.start_time}-${i}`}>
                  <span className="rr-feed-time">{f._live ? 'à l’instant' : formatHm(f.start_time)}</span>
                  <span className={`rr-feed-dir ${inbound ? 'in' : 'out'}`}>{inbound ? '↘' : '↗'}</span>
                  <div className="rr-feed-who">
                    <span className="rr-feed-std">{std}</span>
                    <span className="rr-feed-other rr-live-muted">{f.client_label ? `${f.client_label} · ` : ''}{other}</span>
                  </div>
                  {f.service_code && <span className="rr-chip rr-svc" style={{ '--svc': SERVICE_COLORS[f.service_code] || 'var(--text-muted)' }}>{SERVICE_NOMS[f.service_code] || f.service_code}</span>}
                  <span className="rr-feed-agent">{f.agent_name || ''}</span>
                  <span className={`rr-chip rr-st ${meta.cls}`}>{meta.label}</span>
                  <span className="rr-feed-dur">{dur > 0 ? formatDuration(dur) : ''}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

// ── Modale Référentiel (services SVI + motifs/tags) ──────────────────────────
function ServiceRefModal({ kind, onSwitch, onClose, onSaved }) {
  const isScenario = kind === 'scenarios';
  const keyField = isScenario ? 'scenario_brut' : 'tag_brut';
  const listField = isScenario ? 'scenarios' : 'tags';

  const [rows, setRows] = useState([]);
  const [libelles, setLibelles] = useState([]);
  const [edits, setEdits] = useState({});
  const original = useRef({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/ringover-reporting/config/${kind}`);
      const list = r.data[listField] || [];
      setRows(list);
      setLibelles(r.data.libelles_distincts || []);
      const init = {};
      list.forEach(x => { init[x[keyField]] = x.libelle || ''; });
      setEdits(init);
      original.current = { ...init };
    } catch (e) {
      console.error('Erreur chargement référentiel Ringover:', e);
    } finally {
      setLoading(false);
    }
  }, [kind, keyField, listField]);

  useEffect(() => { load(); }, [load]);

  const dirty = rows
    .filter(x => (edits[x[keyField]] || '') !== (original.current[x[keyField]] || ''))
    .map(x => ({ [keyField]: x[keyField], libelle: edits[x[keyField]] || '' }));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(x =>
      (x[keyField] || '').toLowerCase().includes(q) ||
      (edits[x[keyField]] || '').toLowerCase().includes(q));
  }, [rows, search, edits, keyField]);

  const save = async () => {
    if (!dirty.length) return;
    setSaving(true);
    try {
      await api.put(`/ringover-reporting/config/${kind}`, { items: dirty });
      await load();
      onSaved();
    } catch (e) {
      alert(e.response?.data?.error || 'Erreur d\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Référentiel — normalisation</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="page-tabs" style={{ marginBottom: 12 }}>
            <button type="button" className={isScenario ? 'active' : ''} onClick={() => onSwitch('scenarios')}>🧭 Services (entrants)</button>
            <button type="button" className={!isScenario ? 'active' : ''} onClick={() => onSwitch('tags')}>🏷️ Motifs (tags)</button>
          </div>
          <p className="rr-ref-help">
            {isScenario
              ? 'Regroupez les variantes de service sous un même libellé (ex. « Atelier », « Répondeur atelier », « Accueil SAV » → Atelier / APV). Vide = conservé sous son intitulé brut.'
              : 'Regroupez les variantes de motif sous un même libellé (ex. « RDV », « PRISE DE RDV », « REPROGRAMMATION RDV » → Rendez-vous). Vide = conservé sous son intitulé brut.'}
          </p>
          <input
            className="page-search"
            placeholder="🔍 Rechercher…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          <div className="table-container">
            <table className="rr-conso-table">
              <thead>
                <tr>
                  <th>{isScenario ? 'Service (brut)' : 'Tag (brut)'}</th>
                  <th className="num">Appels</th>
                  <th style={{ width: '42%' }}>Libellé normalisé</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={3} style={{ textAlign: 'center', padding: 16, color: 'var(--text-secondary)' }}>Chargement…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={3} style={{ textAlign: 'center', padding: 16, color: 'var(--text-secondary)' }}>Aucune valeur.</td></tr>
                ) : filtered.map(x => {
                  const v = edits[x[keyField]] || '';
                  const isDirty = v !== (original.current[x[keyField]] || '');
                  return (
                    <tr key={x[keyField]} className={isDirty ? 'rr-row-dirty' : ''}>
                      <td>{x[keyField]}</td>
                      <td className="num">{formatInt(x.nb_appels)}</td>
                      <td>
                        <input
                          type="text"
                          className="rr-ref-input"
                          list="rr-ref-libelles"
                          placeholder={x[keyField]}
                          value={v}
                          onChange={e => setEdits(p => ({ ...p, [x[keyField]]: e.target.value }))}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <datalist id="rr-ref-libelles">
              {libelles.map(l => <option key={l} value={l} />)}
            </datalist>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Fermer</button>
          <button className="btn-primary" onClick={save} disabled={saving || !dirty.length}>
            {saving ? '…' : (dirty.length ? `Enregistrer (${dirty.length})` : 'Aucune modif')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RingoverReporting;
